/**
 * Zod validation schemas for MCP server forms.
 *
 * Used by react-hook-form for create and edit forms.
 */

import { z } from 'zod';

// =============================================================================
// Template Create/Edit Schema
// =============================================================================

const jsonStringSchema = z.string().refine(
  (val) => {
    if (!val || val.trim() === '') return true;
    try {
      JSON.parse(val);
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Invalid JSON syntax' }
);

export const mcpTemplateFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  slug: z
    .string()
    .min(3, 'Slug must be at least 3 characters')
    .max(50)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Must be lowercase alphanumeric with hyphens (e.g. servicenow-itsm)'),
  display_name: z.string().min(1, 'Display name is required').max(255),
  description: z.string().max(2000).optional().or(z.literal('')),
  icon: z.string().max(50).optional().or(z.literal('')),
  category: z.enum(['infrastructure', 'itsm', 'monitoring', 'communication', 'knowledge', 'custom']),
  config_schema: jsonStringSchema,
  secrets_schema: jsonStringSchema,
  base_policies: jsonStringSchema,
  default_tools: jsonStringSchema,
  tool_discovery_mode: z.enum(['static', 'dynamic']).default('static'),
  documentation_url: z.string().max(500).optional().or(z.literal('')),
  setup_instructions: z.string().optional().or(z.literal('')),
  is_beta: z.boolean().default(false),
  execution_tier: z.enum(['platform_http', 'agent_managed', 'platform_native']).default('platform_http'),
  template_version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Must be semver (e.g. 1.0.0)').default('1.0.0'),
  tags: z.array(z.string()).default([]),
  setup_complexity: z.enum(['simple', 'intermediate', 'advanced']).default('simple'),
});

export type MCPTemplateForm = z.infer<typeof mcpTemplateFormSchema>;

export const defaultMCPTemplateFormValues: MCPTemplateForm = {
  name: '',
  slug: '',
  display_name: '',
  description: '',
  icon: '',
  category: 'custom',
  config_schema: JSON.stringify({ type: 'object', properties: {}, required: [] }, null, 2),
  secrets_schema: JSON.stringify({}, null, 2),
  base_policies: JSON.stringify({
    max_calls_per_turn: 5,
    max_timeout_seconds: 30,
    rate_limit_per_minute: 30,
    rate_limit_per_hour: 300,
    require_https: true,
  }, null, 2),
  default_tools: JSON.stringify([], null, 2),
  tool_discovery_mode: 'static',
  documentation_url: '',
  setup_instructions: '',
  is_beta: false,
  execution_tier: 'platform_http',
  template_version: '1.0.0',
  tags: [],
  setup_complexity: 'simple',
};

// =============================================================================
// Create Schema
// =============================================================================

export const mcpServerCreateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().max(1000).optional().or(z.literal('')),
  endpoint_url: z
    .string()
    .min(1, 'Endpoint URL is required')
    .max(2000)
    .refine(
      (v) => v.startsWith('http://') || v.startsWith('https://'),
      'Must start with http:// or https://'
    ),
  auth_type: z.enum(['none', 'bearer', 'api_key', 'mtls', 'oauth2_cc']).default('none'),
  execution_tier: z.enum(['platform_http', 'byos', 'local_agent']).default('byos'),
  timeout_seconds: z.coerce.number().min(5).max(300).default(30),
  auto_discover_tools: z.boolean().default(true),
});

export type MCPServerCreateForm = z.infer<typeof mcpServerCreateSchema>;

// =============================================================================
// Update Schema
// =============================================================================

export const mcpServerUpdateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).optional().or(z.literal('')),
  endpoint_url: z
    .string()
    .min(1)
    .max(2000)
    .refine(
      (v) => v.startsWith('http://') || v.startsWith('https://'),
      'Must start with http:// or https://'
    )
    .optional(),
  auth_type: z.enum(['none', 'bearer', 'api_key', 'mtls', 'oauth2_cc']).optional(),
  timeout_seconds: z.coerce.number().min(5).max(300).optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

export type MCPServerUpdateForm = z.infer<typeof mcpServerUpdateSchema>;

// =============================================================================
// Credentials Schema
// =============================================================================

export const mcpServerCredentialsSchema = z.object({
  bearer_token: z.string().optional().or(z.literal('')),
  api_key: z.string().optional().or(z.literal('')),
  oauth2_client_id: z.string().optional().or(z.literal('')),
  oauth2_client_secret: z.string().optional().or(z.literal('')),
  oauth2_token_url: z.string().url().optional().or(z.literal('')),
});

export type MCPServerCredentialsForm = z.infer<typeof mcpServerCredentialsSchema>;

// =============================================================================
// Default Form Values
// =============================================================================

export const defaultMCPServerFormValues: MCPServerCreateForm = {
  name: '',
  description: '',
  endpoint_url: '',
  auth_type: 'none',
  execution_tier: 'byos',
  timeout_seconds: 30,
  auto_discover_tools: true,
};
